import type { ReactNode } from 'react';
import React, { useState } from 'react';
import { useAuthQuery } from '@packages/shared/src/hooks/useAuthQuery/useAuthQuery';
import { CustomerBookingsDocument } from '@packages/gql/generated/shopping/CustomerBookingsDocument';
import { Skeleton } from '@packages/shared/src/ui/Skeleton/Skeleton';
import { Notification } from '@packages/shared/src/ui/Notification/Notification';
import type { MessageDescriptor } from 'react-intl';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Card, CardContent, CardFooter, CardHeader } from '@packages/shared/src/ui/Card/Card';
import type { CustomerBookingType } from '@packages/gql/generated/shopping/graphql';
import { getLocalizedPath } from '@packages/shared/src/utils/getLocalizedPath/getLocalizedPath';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { Select, SelectContent, SelectItem } from '@packages/shared/src/ui/Select/Select';
import { Link } from '@packages/modules/src/Link/Link';
import { buttonVariants } from '@packages/shared/src/ui/Button/Button.variants';
import { PriceDelta } from '../PriceDelta/PriceDelta.client';
import { PaylinkCard } from '../Paylink/PaylinkCard.client';
import { Maturities } from '../Maturities/Maturities.client';
import { InvoiceDetail } from '../Invoice/InvoiceDetail.client';
import { OfflineNotification } from '../OfflineNotification/OfflineNotification';

/* GraphQL */ `
  query CustomerBookings($type: CustomerBookingType, $paginationKey: String) {
    customerBookings(type: $type, paginationKey: $paginationKey) {
      bookings {
        id
        type
        name
        date
        dateFormatted
        amount {
          currencyCode
          value
        }
        isAmountNegative
        additionalText
        linkId
        linkText
      }
      balance {
        currencyCode
        value
      }
      dueAmount {
        currencyCode
        value
      }
      financialDetails {
        minimumRate
        rdiAmount
        totalMinimumRate
        minimumRateDueDate
        isRdiAmountExcluded
      }
      paylink
      nextPaginationKey
    }
  }
`;

const messages = defineMessages({
  invoice: {
    id: 'CustomerBookings.Filter.Invoice',
    defaultMessage: 'Rechnung',
  },
  reimbursement: {
    id: 'CustomerBookings.Filter.Reimbursement',
    defaultMessage: 'Rücksendung - Gutschrift',
  },
  payment: {
    id: 'CustomerBookings.Filter.Payment',
    defaultMessage: 'Zahlung',
  },
  payout: {
    id: 'CustomerBookings.Filter.Payout',
    defaultMessage: 'Auszahlung',
  },
  other: {
    id: 'CustomerBookings.Filter.Other',
    defaultMessage: 'sonstige Transaktionen',
  },
  additionalCosts: {
    id: 'CustomerBookings.Filter.AdditionalCosts',
    defaultMessage: 'weitere Kosten',
  },
  interests: {
    id: 'CustomerBookings.Filter.Interests',
    defaultMessage: 'Zinsen',
  },
});

const typeMapping: Record<CustomerBookingType, { text: MessageDescriptor; icon: ReactNode }> = {
  Invoice: {
    icon: <span className="icon-emp-billing size-4" />,
    text: messages.invoice,
  },
  Reimbursement: {
    icon: <span className="icon-emp-reimbursement-40 size-4" />,
    text: messages.reimbursement,
  },
  Payment: {
    icon: <span className="icon-emp-payment-40 size-4" />,
    text: messages.payment,
  },
  Payout: {
    icon: <span className="icon-emp-payment-40 size-4" />,
    text: messages.payout,
  },
  Other: {
    icon: <span className="icon-emp-othertransaction-40 size-4" />,
    text: messages.other,
  },
  AdditionalCosts: {
    icon: <span className="icon-emp-paymentmethods size-4" />,
    text: messages.additionalCosts,
  },
  Interests: {
    icon: <span className="icon-emp-interests-40 size-4" />,
    text: messages.interests,
  },
};

// Separate component for rendering a list of bookings (similar to InvoiceList)
const BookingsList = ({
  typeFilter,
  paginationKey,
  isLastPage = false,
  onAddPaginationKey,
  onShowEInvoice,
}: {
  typeFilter: CustomerBookingType | 'All';
  paginationKey?: string;
  isLastPage?: boolean;
  onAddPaginationKey: (key: string) => void;
  onShowEInvoice: (id: string) => void;
}) => {
  const [{ data, error }] = useAuthQuery({
    query: CustomerBookingsDocument,
    variables: {
      type: typeFilter === 'All' ? null : typeFilter,
      paginationKey,
    },
  });

  const handleLoadMore = () => {
    if (data?.customerBookings.nextPaginationKey) {
      onAddPaginationKey(data.customerBookings.nextPaginationKey);
    }
  };

  if (error) {
    return <OfflineNotification />;
  }

  if (!data) {
    return (
      <div className="space-y-2" data-testid="bookings-list-loading">
        {Array.from({ length: 4 }, (_, i) => `skeleton-${paginationKey || 'initial'}-${i}`).map(
          (key) => (
            <Skeleton key={key} className="h-20 w-full rounded-lg" />
          ),
        )}
      </div>
    );
  }

  const bookingsToDisplay = data.customerBookings.bookings || [];

  return (
    <>
      {bookingsToDisplay.length === 0 && !paginationKey && (
        <Notification severity="info" variant="outline" className="mt-2">
          <FormattedMessage
            id="CustomerBookings.NoBookings"
            defaultMessage="Leider gibt es mit den aktuellen Filtereinstellungen keine Ergebnisse."
          />
        </Notification>
      )}
      {bookingsToDisplay.map((booking, index) => (
        <div key={booking.id}>
          {booking.dateFormatted !== bookingsToDisplay[index - 1]?.dateFormatted && (
            <div className="text-size-body-sm mt-4 mb-1 font-bold first:mt-1.5">
              {booking.dateFormatted}
            </div>
          )}
          <Card className="text-size-body-sm mt-1.5 flex items-center gap-2 p-1.5">
            {typeMapping[booking.type]?.icon && typeMapping[booking.type].icon}
            <div className="grow">
              <div className="text-size-body-sm font-bold">{booking.name}</div>
              <div className="text-size-body-xs">{booking.additionalText}</div>
              {booking.linkId && booking.linkText && (
                <Button
                  className="text-size-body-xs py-[2px]"
                  variant="link"
                  onClick={() => onShowEInvoice(booking.linkId!)}
                >
                  {booking.linkText}
                </Button>
              )}
            </div>
            <div>
              <PriceDelta
                value={booking.amount.value}
                currencyCode={booking.amount.currencyCode}
                isNegative={booking.isAmountNegative}
              />
            </div>
          </Card>
        </div>
      ))}
      {isLastPage && !!data.customerBookings.nextPaginationKey && (
        <div className="mt-4 flex justify-center">
          <Button variant="color-button" size="medium" onClick={handleLoadMore}>
            <FormattedMessage
              id="CustomerBookings.LoadMore"
              defaultMessage="Weitere Transaktionen laden"
            />
            <span className="icon-emp-load-reload ml-2 size-4" />
          </Button>
        </div>
      )}
    </>
  );
};

export const CustomerBookings = () => {
  const [typeFilter, setTypeFilter] = React.useState<CustomerBookingType | 'All'>('All');
  const [showEInvoiceNumber, setShowEInvoiceNumber] = useState<string | null>(null);
  const [paginationKeys, setPaginationKeys] = useState<string[]>([]);

  const {
    order: {
      paths: { myAccountPaymentsDates, myAccountInvoices },
      supportedPaths: { myAccountPaymentsDates: paymentDatesAllowed },
    },
  } = useConfig();
  const { formatMessage, locale } = useIntl();

  // Fetch initial data to get balance, dueAmount, and paylink
  const [{ data, error, fetching }] = useAuthQuery({
    query: CustomerBookingsDocument,
    variables: { type: typeFilter === 'All' ? null : typeFilter },
  });

  // Reset pagination when filter changes
  React.useEffect(() => {
    setPaginationKeys([]);
  }, [typeFilter]);

  const handleAddPaginationKey = (key: string) => {
    setPaginationKeys((prev) => [...prev, key]);
  };

  // Check for errors first
  // Show unified error notification for: GraphQL errors or DiVA errors
  if (error || (data && data?.customerBookings.__typename !== 'CustomerBookingsResult')) {
    return <OfflineNotification />;
  }

  // Handle loading state
  if (fetching || !data) {
    return (
      <div className="bg-color-grey-light my-2 flex flex-col gap-2 [&>div]:h-[5rem]">
        {Array.from({ length: 11 }, (_, i) => (
          <Skeleton key={i} />
        ))}
      </div>
    );
  }

  return (
    <>
      {showEInvoiceNumber && (
        <InvoiceDetail
          invoiceNumber={showEInvoiceNumber}
          onClose={() => setShowEInvoiceNumber(null)}
        />
      )}
      <div className="bg-color-grey-light flex flex-col justify-between gap-2 p-1.5 md:flex-row md:p-3">
        <Maturities
          className="text-size-body-sm w-full md:w-[50%]"
          balance={data.customerBookings.balance}
          financialDetails={data.customerBookings.financialDetails}
          cardHeadline={
            <h3 className="text-size-headline-base">
              <FormattedMessage
                id="CustomerBookings.Maturities.CardHeadline"
                defaultMessage="Rechnungen / Zahlungen"
              />
            </h3>
          }
        />
        {data.customerBookings.paylink && data.customerBookings.dueAmount && (
          <PaylinkCard
            className="w-full md:w-[50%]"
            url={data.customerBookings.paylink}
            amount={data.customerBookings.dueAmount}
          />
        )}
      </div>
      <h2 className="text-color-text-dark text-size-headline-xl md:text-size-headline-2xl my-1.5 pl-2">
        {formatMessage({
          id: 'CustomerBookings.HeadlinePaymentsAndInvoices',
          defaultMessage: 'Meine Zahlungen und Rechnungen',
        })}
      </h2>
      <div className="bg-color-grey-light flex flex-col gap-2 p-1.5 md:flex-row md:p-3">
        {paymentDatesAllowed && (
          <Card className="md:w-[50%]">
            <CardHeader hasDivider>
              <h3 className="text-size-headline-base">
                <FormattedMessage
                  id="CustomerBookings.PaymentDates.CardHeader"
                  defaultMessage="Nächste Zahltermine"
                />
              </h3>
            </CardHeader>
            <CardContent>
              <div className="text-size-body-sm flex items-center gap-2">
                <span className="icon-emp-payment-dates size-4 shrink-0" />
                <FormattedMessage
                  id="CustomerBookings.PaymentDates.Text"
                  defaultMessage="Hier findest du eine Übersicht über deine nächsten Zahltermine."
                />
              </div>
            </CardContent>
            <CardFooter className="justify-end py-2">
              <Link
                href={getLocalizedPath(myAccountPaymentsDates, locale)}
                className={buttonVariants({ size: 'medium', variant: 'color-button' })}
              >
                <FormattedMessage
                  id="CustomerBookings.PaymentDates.Button"
                  defaultMessage="Nächste Zahltermine anzeigen"
                />
              </Link>
            </CardFooter>
          </Card>
        )}
        <Card className="md:w-[50%]">
          <CardHeader hasDivider>
            <h3 className="text-size-headline-base">
              <FormattedMessage
                id="CustomerBookings.Invoices.CardHeader"
                defaultMessage="Rechnungen"
              />
            </h3>
          </CardHeader>
          <CardContent>
            <div className="text-size-body-sm items-top flex gap-2">
              <span className="icon-emp-billing size-5 shrink-0" />
              <FormattedMessage
                id="CustomerBookings.Invoices.Text"
                defaultMessage="Hier findest du eine Übersicht über deine abgeschlossenen und offenen Rechnungen."
              />
            </div>
          </CardContent>
          <CardFooter className="justify-end py-2">
            <Link
              href={getLocalizedPath(myAccountInvoices, locale)}
              className={buttonVariants({ size: 'medium', variant: 'color-button' })}
            >
              <FormattedMessage
                id="CustomerBookings.Invoices.Button"
                defaultMessage="Rechnungen anzeigen"
              />
            </Link>
          </CardFooter>
        </Card>
      </div>
      <h2 className="text-color-text-dark text-size-headline-xl md:text-size-headline-2xl my-1.5 pl-2">
        <FormattedMessage id="CustomerBookings.Headline" defaultMessage="Meine Transaktionen" />
      </h2>
      <div className="bg-color-grey-light flex flex-col p-1.5 md:p-3">
        <div className="text-size-body-sm">
          <FormattedMessage
            id="CustomerBookings.Infotext"
            defaultMessage="Hier findest du alle Transaktionen und wie sich dein Saldo zusammensetzt."
          />
        </div>
        <div className="my-1.5 max-w-xs">
          <Select
            label={formatMessage({
              id: 'CustomerBookings.Filter.Label',
              defaultMessage: 'Art',
            })}
            value={typeFilter}
            onValueChange={(value) => setTypeFilter(value as CustomerBookingType)}
          >
            <SelectContent>
              <SelectItem value="All">
                <FormattedMessage id="CustomerBookings.Filter.All" defaultMessage="Alle" />
              </SelectItem>
              {Object.entries(typeMapping).map(([key, { text }]) => (
                <SelectItem key={key} value={key}>
                  {formatMessage(text)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Render initial bookings list */}
        <BookingsList
          typeFilter={typeFilter}
          isLastPage={paginationKeys.length === 0}
          onAddPaginationKey={handleAddPaginationKey}
          onShowEInvoice={setShowEInvoiceNumber}
        />

        {/* Render additional pages */}
        {paginationKeys.map((key, index) => (
          <BookingsList
            key={key}
            typeFilter={typeFilter}
            paginationKey={key}
            isLastPage={index === paginationKeys.length - 1}
            onAddPaginationKey={handleAddPaginationKey}
            onShowEInvoice={setShowEInvoiceNumber}
          />
        ))}
      </div>
    </>
  );
};
